/* Pure helpers behind the risk band, the both-books counters and the manual
 * row's "≈ net" figure. No React, no fetching — tested in riskMath.test.js. */

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isOpen = (p) => p && (p.status === "OPEN" || p.status === "PENDING");

/** The stop that actually protects the row: the trail only ever ratchets UP. */
export function effectiveStop(p) {
  const sl = num(p?.stop_loss);
  const tr = num(p?.trailing_sl);
  if (sl == null && tr == null) return null;
  return Math.max(sl ?? -Infinity, tr ?? -Infinity);
}

/** ₹ at risk on one OPEN row: (entry − effective stop) × qty, never negative.
 *  PENDING rows have no fill yet; rows without a stop are counted as
 *  premium-at-risk (the whole outlay can go). */
export function rowRisk(p) {
  if (!p || p.status === "PENDING") return 0;
  const entry = num(p.entry_price);
  const qty = num(p.quantity) ?? 0;
  if (entry == null || qty <= 0) return 0;
  const stop = effectiveStop(p);
  const dist = stop == null ? entry : entry - stop;
  return Math.max(0, dist) * qty;
}

/** Σ rowRisk over every open row (both books, any strategy). */
export function openRisk(rows) {
  return (rows || []).filter(isOpen).reduce((s, p) => s + rowRisk(p), 0);
}

/** Loss budget left today = limit − losses so far (profit does not extend it). */
export function lossBudgetLeft(limit, todayPnl) {
  const L = num(limit);
  if (L == null || L <= 0) return null;
  const pnl = num(todayPnl) ?? 0;
  return Math.max(0, L - Math.max(0, -pnl));
}

/** Open / live / paper counts across both books. */
export function countBooks(rows) {
  const open = (rows || []).filter(isOpen);
  const live = open.filter((p) => p.is_paper === false || p.is_paper === 0).length;
  return { open: open.length, live, paper: open.length - live };
}

/** The whole band in one call: what the TopBar / AutoTrader stat row show. */
export function riskBand(status, rows) {
  const list = rows || status?.open_trades || [];
  return {
    budgetLeft: lossBudgetLeft(status?.limits?.max_daily_loss, status?.today_pnl),
    openRisk: openRisk(list),
    ...countBooks(list),
  };
}

/** armed / paused / halted from /autotrade/status; null when unknown. */
export function engineState(status) {
  if (!status) return null;
  if (status.kill_switch) return "halted";
  if (!status.autotrade_enabled) return "paused";
  return "armed";
}

/* Round-trip charges for buying then selling `qty` option units — the same
 * arithmetic as backend/app/core/costs.py (Zerodha F&O, rates from
 * 2026-04-01) so the manual row's "≈ net" matches what a close will book. */
export const COST = {
  BROKERAGE_PER_ORDER: 20.0,
  STT_SELL_RATE: 0.0015,
  EXCHANGE_TXN_RATE: 0.00035,
  SEBI_RATE: 0.000001,
  STAMP_BUY_RATE: 0.00003,
  GST_RATE: 0.18,
};

export function roundTripCharges(entry, exit, qty) {
  const q = Math.max(0, num(qty) ?? 0);
  if (q <= 0) return 0;
  const buy = Math.max(0, num(entry) ?? 0) * q;
  const sell = Math.max(0, num(exit) ?? 0) * q;
  const brokerage = 2 * COST.BROKERAGE_PER_ORDER;
  const stt = COST.STT_SELL_RATE * sell;
  const exch = COST.EXCHANGE_TXN_RATE * (buy + sell);
  const sebi = COST.SEBI_RATE * (buy + sell);
  const stamp = COST.STAMP_BUY_RATE * buy;
  const gst = COST.GST_RATE * (brokerage + exch + sebi);
  return brokerage + stt + exch + sebi + stamp + gst;
}

/** Gross → net for an open row: uses the row's own `charges` when the API
 *  carries one, else the estimate above. `estimated` says which. */
export function netPnl(p, ltp) {
  const entry = num(p?.entry_price);
  const L = num(ltp);
  const qty = num(p?.quantity) ?? 0;
  if (entry == null || L == null || qty <= 0) return null;
  const gross = (L - entry) * qty;
  const own = num(p?.charges);
  const charges = own != null ? own : roundTripCharges(entry, L, qty);
  return { gross, charges, net: gross - charges, estimated: own == null };
}

/** Unrealised P&L over OPEN rows priced by a `EXCHANGE:SYMBOL → ltp` map.
 *  `total` is gross, `net` is after round-trip charges (the row's own
 *  `charges` when present, else the estimate above), `priced` how many rows
 *  had a quote. `keep` optionally narrows the rows (e.g. one mode's book). */
export function unrealisedFromQuotes(rows, quotes, keep = null) {
  let total = 0;
  let net = 0;
  let priced = 0;
  for (const p of rows || []) {
    if (!p || p.status !== "OPEN") continue;
    if (keep && !keep(p)) continue;
    const key = p.tradingsymbol && p.exchange ? `${p.exchange}:${p.tradingsymbol}` : null;
    const r = key ? netPnl(p, quotes?.[key]) : null;
    if (!r) continue;
    total += r.gross;
    net += r.net;
    priced += 1;
  }
  return { total, net, priced };
}
